import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { User, Bell, Globe, Sun } from "lucide-react";
import { getAlertSettings, updateAlertSettings, getAccountUsage, redeemShopifyConnectId } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export default function SettingsPage() {
  const { user, plan, planMeta, aiCallsThisSession } = useAuth();

  const [name, setName] = useState(user?.user_metadata?.name || "");
  const [email] = useState(user?.email || "");
  const [newAdAlerts, setNewAdAlerts] = useState(true);
  const [scalingAlerts, setScalingAlerts] = useState(true);
  const [removedAlerts, setRemovedAlerts] = useState(false);
  const [keywordAlerts, setKeywordAlerts] = useState(true);
  const [defaultCountry, setDefaultCountry] = useState("us");
  const [defaultPlatform, setDefaultPlatform] = useState("all");
  const [theme, setTheme] = useState("light");
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [competitorCount, setCompetitorCount] = useState(0);
  const [aiCallsToday, setAiCallsToday] = useState(0);
  const [connectId, setConnectId] = useState("");
  const [connectStatus, setConnectStatus] = useState(null); // "success" | "error" | null
  const [connectMessage, setConnectMessage] = useState("");
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadUsage = async () => {
      try {
        const usage = await getAccountUsage();
        if (cancelled || !usage) return;
        setCompetitorCount(usage.total_tracked_competitors ?? 0);
        setAiCallsToday(usage.ai_calls_today ?? 0);
      } catch {
        if (!cancelled) {
          setCompetitorCount(0);
          setAiCallsToday(0);
        }
      }
    };
    loadUsage();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const prefs = await getAlertSettings();
        if (cancelled || !prefs) return;
        setNewAdAlerts(Boolean(prefs.new_ad));
        setScalingAlerts(Boolean(prefs.scaling));
        setRemovedAlerts(Boolean(prefs.removed));
        setKeywordAlerts(Boolean(prefs.keyword));
      } catch {
        // Fall back to sensible defaults if settings are not yet stored.
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!savingAlerts) {
      const timeout = setTimeout(async () => {
        try {
          setSavingAlerts(true);
          await updateAlertSettings({
            new_ad: newAdAlerts,
            scaling: scalingAlerts,
            removed: removedAlerts,
            keyword: keywordAlerts,
          });
        } finally {
          if (!cancelled) {
            setSavingAlerts(false);
          }
        }
      }, 400);

      return () => {
        cancelled = true;
        clearTimeout(timeout);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [newAdAlerts, scalingAlerts, removedAlerts, keywordAlerts, savingAlerts]);

  return (
    <div className="fade-in" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">Settings</h1>
        <p className="text-sm text-gray-500">Manage your account and preferences.</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-gray-100/70 border border-gray-200 p-1 h-auto">
          <TabsTrigger value="profile" className="text-xs data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-500 px-3 py-1.5" data-testid="tab-profile">
            <User className="w-3.5 h-3.5 mr-1.5" />Profile
          </TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-500 px-3 py-1.5" data-testid="tab-notifications">
            <Bell className="w-3.5 h-3.5 mr-1.5" />Notifications
          </TabsTrigger>
          <TabsTrigger value="defaults" className="text-xs data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-500 px-3 py-1.5" data-testid="tab-defaults">
            <Globe className="w-3.5 h-3.5 mr-1.5" />Defaults
          </TabsTrigger>
          <TabsTrigger value="appearance" className="text-xs data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-500 px-3 py-1.5" data-testid="tab-appearance">
            <Sun className="w-3.5 h-3.5 mr-1.5" />Appearance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-lg" data-testid="profile-settings">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Profile Information</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Full Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary/40 focus:ring-2 focus:ring-primary/15 focus:outline-none rounded-lg h-9 px-3 w-full transition-all"
                  data-testid="settings-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 rounded-lg h-9 px-3 w-full transition-all cursor-not-allowed"
                  data-testid="settings-email-input"
                />
              </div>
              <div className="border-t border-gray-100 pt-4 mt-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Account</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                    <div className="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Current Plan</div>
                    <div className="text-sm font-semibold text-gray-900 capitalize">{planMeta.label}</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                    <div className="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Tracked Competitors</div>
                    <div className="text-sm font-semibold text-gray-900">
                      {competitorCount} / {planMeta.maxCompetitors}
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 col-span-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[11px] text-gray-400 uppercase tracking-wider mb-1">AI Calls Today</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {aiCallsToday} / {planMeta.maxAiCallsPerDay}
                        </div>
                      </div>
                      <a
                        href="https://Radar.app/pricing"
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary hover:text-primary/90 font-medium"
                      >
                        View plans
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-radar-secondary h-9 px-4 text-sm font-medium shadow-sm" data-testid="save-profile-btn">
                Save Changes
              </Button>

              <div className="border-t border-gray-100 pt-6 mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Connect a Shopify store (by ID)</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Paste the 32-character Connect ID you received from the Shopify app, then link it to your Radar account.
                </p>

                <div className="space-y-2">
                  <input
                    value={connectId}
                    onChange={(e) => setConnectId(e.target.value.trim())}
                    placeholder="Paste Connect ID (32 hex chars)"
                    className="bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 rounded-lg h-9 px-3 w-full transition-all"
                    disabled={linking}
                    data-testid="connect-id-input"
                  />
                  {connectStatus && (
                    <div
                      className={`text-xs px-3 py-2 rounded-lg ${
                        connectStatus === "success"
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-red-50 text-red-700 border border-red-200"
                      }`}
                      data-testid="connect-id-status"
                    >
                      {connectMessage}
                    </div>
                  )}

                  <button
                    onClick={async () => {
                      setLinking(true);
                      setConnectStatus(null);
                      setConnectMessage("");
                      try {
                        const data = await redeemShopifyConnectId(connectId);
                        if (data?.success) {
                          setConnectStatus("success");
                          setConnectMessage("Store linked successfully. You can refresh the Shopify app Settings.");
                        } else {
                          setConnectStatus("error");
                          setConnectMessage(data?.message || data?.error || "Failed to link store");
                        }
                      } catch (e) {
                        setConnectStatus("error");
                        setConnectMessage(e?.message || "Failed to link store");
                      } finally {
                        setLinking(false);
                      }
                    }}
                    disabled={linking || connectId.length !== 32}
                    className="w-full h-9 rounded-lg bg-primary text-primary-foreground hover:bg-radar-secondary disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    data-testid="connect-id-link-btn"
                  >
                    {linking ? "Linking..." : "Link Store"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-lg" data-testid="notification-settings">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Notification Preferences</h3>
            <div className="space-y-4">
              {[
                { label: "New ad alerts", desc: "When competitors launch new creatives", value: newAdAlerts, onChange: setNewAdAlerts, id: "new-ad" },
                { label: "Scaling alerts", desc: "When ads hit 30+ day milestones", value: scalingAlerts, onChange: setScalingAlerts, id: "scaling" },
                { label: "Removed ad alerts", desc: "When competitors remove creatives", value: removedAlerts, onChange: setRemovedAlerts, id: "removed" },
                { label: "Keyword trend alerts", desc: "When new keyword trends emerge", value: keywordAlerts, onChange: setKeywordAlerts, id: "keyword" },
              ].map((pref) => (
                <div key={pref.id} className="flex items-center justify-between py-1">
                  <div>
                    <div className="text-sm text-gray-900">{pref.label}</div>
                    <div className="text-xs text-gray-400">{pref.desc}</div>
                  </div>
                  <Switch checked={pref.value} onCheckedChange={pref.onChange} className="data-[state=checked]:bg-primary" data-testid={`switch-${pref.id}`} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="defaults">
          <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-lg" data-testid="defaults-settings">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Default Preferences</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Default Country</label>
                <Select value={defaultCountry} onValueChange={setDefaultCountry}>
                  <SelectTrigger className="w-full h-9 text-sm bg-white border-gray-200 text-gray-700" data-testid="default-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="us" className="text-sm">United States</SelectItem>
                    <SelectItem value="uk" className="text-sm">United Kingdom</SelectItem>
                    <SelectItem value="ca" className="text-sm">Canada</SelectItem>
                    <SelectItem value="au" className="text-sm">Australia</SelectItem>
                    <SelectItem value="de" className="text-sm">Germany</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Default Platform</label>
                <Select value={defaultPlatform} onValueChange={setDefaultPlatform}>
                  <SelectTrigger className="w-full h-9 text-sm bg-white border-gray-200 text-gray-700" data-testid="default-platform-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all" className="text-sm">All Platforms</SelectItem>
                    <SelectItem value="meta" className="text-sm">Meta</SelectItem>
                    <SelectItem value="tiktok" className="text-sm">TikTok</SelectItem>
                    <SelectItem value="google" className="text-sm">Google</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="appearance">
          <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-lg" data-testid="appearance-settings">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Theme</h3>
            <div className="space-y-3">
              {[
                { value: "light", label: "Light", desc: "Clean white background (default)" },
                { value: "dark", label: "Dark", desc: "Dark background for low-light environments" },
              ].map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTheme(t.value)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all ${
                    theme === t.value ? "border-primary/30 bg-primary/[0.06] ring-2 ring-primary/15" : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid={`theme-${t.value}`}
                >
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-900">{t.label}</div>
                    <div className="text-xs text-gray-400">{t.desc}</div>
                  </div>
                  {theme === t.value && (
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
